import React, { useContext } from "react";
import { DefaultButton, FontIcon, Spinner, SpinnerSize } from "@fluentui/react";
import { ThemeContext } from "@fluentui/react-theme-provider";
import { ThemeProvider } from "styled-components";
import DocumentTitle from "react-document-title";
import DropdownSelect from "react-dropdown-select";
import CountryPrefixes from "@/utils/CountryPrefixes";
import CompanyOnboardingStepper, { CompanyOnboardingStep } from "@/screen/Companies/components/CompanyOnboardingStepper";
import CompanySectionCard from "@/screen/Companies/components/CompanySectionCard";
import CompanyDetailsSectionNav from "@/screen/Companies/CompanyDetails/CompanyDetailsSectionNav";
import { DocumentUploadBlock } from "@/screen/Companies/CompanyDetails/CompanyDocumentsSection";
import { CompanyKycSections } from "@/screen/Companies/CompanyKyc/companyKycSections";
import { useCompanyKycController } from "@/screen/Companies/CompanyKyc/useCompanyKycController";
import { AnnualGrossIncomeOptions, EconomicActivityOptions, FundsOriginInvestments, FundsOriginOptions, FundsOriginOther } from "@/screen/Companies/CompanyKyc/companyKycForm";
import type { CompanyKycField, CompanyKycTextField, CompanyKycYesNoField } from "@/screen/Companies/CompanyKyc/companyKycForm";
import { SearchableSelectWrapper } from "@/screen/Identities/IdentityFiscalInputs/Styles/IdentityFiscalInputsStyles";
import { CompanyOnboardingStrings } from "@/screen/Companies/companyOnboarding.strings";
import { Cont, MainCont, BackLink, Header, ErrorNotice, SubmitNotice, InfoBanner, ResultPanel, ResultActions } from "@/screen/Companies/Styles/CompanyOnboardingStyles";
import { Field, FieldError, FieldGrid, Input, Select, TextArea } from "@/screen/Companies/Styles/CompanyFormFieldStyles";
import { CheckRow, DetailsFooter, DetailsLayout, FormFieldset, Pill, YesNoRow } from "@/screen/Companies/Styles/CompanyDetailsStyles";

interface MultiOption
{
    value: string;
    label: string;
}

// Los países de riesgo se guardan por su nombre (Omnia los recibe como texto)
const CountryOptions: MultiOption[] = CountryPrefixes
    .filter((country) => country.name)
    .map((country) => ({ value: country.name, label: country.name }));

const FundsOriginMultiOptions: MultiOption[] = FundsOriginOptions.map((option) => ({ value: option.value, label: option.text }));

// Ancla de cada sección para el índice lateral (1 = PEP ... 5 = FATCA)
const sectionId = (sectionNumber: number): string => CompanyKycSections[sectionNumber - 1].id;

// Paso 4 del alta de empresa: KYC (conocer al cliente). Queda como borrador en IronIA; al enviarlo a bastanteo se
// manda todo el alta a Omnia.
const CompanyKycScreen: React.FC = () =>
{
    const themeContext = useContext(ThemeContext);
    const controller = useCompanyKycController();
    const {phase, formData} = controller;

    const required = (label: string): React.ReactNode => <span>{label} <b>*</b></span>;
    const missing = (field: CompanyKycField): React.ReactNode =>
        controller.isMissing(field) ? <FieldError>Este dato es obligatorio o no es válido.</FieldError> : null;
    const textInput = (name: CompanyKycTextField, maxLength: number, type: string = "text", placeholder?: string): React.ReactNode =>
        <Input type={type} name={name} value={formData?.[name] ?? ""} onChange={controller.handleTextChange} maxLength={maxLength} placeholder={placeholder} autoComplete="off" min={type === "number" ? 0 : undefined} />;
    const yesNo = (field: CompanyKycYesNoField, question: string): React.ReactNode =>
    (
        <YesNoRow role="radiogroup" aria-label={question}>
            <span>{question}</span>
            <div>
                <Pill $checked={formData?.[field] === true}>
                    <input type="radio" name={field} checked={formData?.[field] === true} onChange={() => controller.handleYesNoChange(field, true)} /> Sí
                </Pill>
                <Pill $checked={formData?.[field] === false}>
                    <input type="radio" name={field} checked={formData?.[field] === false} onChange={() => controller.handleYesNoChange(field, false)} /> No
                </Pill>
            </div>
        </YesNoRow>
    );
    const multiSelect = (name: string, options: MultiOption[], values: string[], placeholder: string, invalid: boolean, onChange: (values: string[]) => void): React.ReactNode =>
    (
        <SearchableSelectWrapper $invalid={invalid} $disabled={!controller.isEditable}>
            <DropdownSelect<MultiOption>
                options={options}
                values={options.filter((option) => values.includes(option.value))}
                onChange={(selected) => onChange(selected.map((option) => option.value))}
                placeholder={placeholder}
                multi
                searchable
                clearable={false}
                dropdownPosition="bottom"
                dropdownHeight="260px"
                labelField="label"
                valueField="value"
                searchBy="label"
                disabled={!controller.isEditable}
                name={name}
                noDataLabel="No hay resultados"
            />
        </SearchableSelectWrapper>
    );

    return(
        <DocumentTitle title={`IronIA - ${CompanyOnboardingStrings.kycTitle}`}>
            <Cont theme={themeContext}>
                <ThemeProvider theme={themeContext}>
                    <MainCont>
                        <BackLink type="button" onClick={controller.goToIdentities}>
                            <FontIcon iconName="ChevronLeft" />
                            {CompanyOnboardingStrings.backToIdentities}
                        </BackLink>
                        <Header>
                            <h1>{CompanyOnboardingStrings.kycTitle}</h1>
                            <h2>{CompanyOnboardingStrings.kycSubtitle}</h2>
                            {(controller.companyName || controller.taxId) && <p>{[controller.companyName, controller.taxId].filter(Boolean).join(" · ")}</p>}
                        </Header>
                        <CompanyOnboardingStepper currentStep={CompanyOnboardingStep.CompanyKyc} />

                        {phase === "loading" && <Spinner size={SpinnerSize.large} label={CompanyOnboardingStrings.kycLoading} />}
                        {phase === "error" && <ErrorNotice role="alert"><p>{controller.loadError}</p></ErrorNotice>}

                        {phase === "done" &&
                        (
                            <ResultPanel role="status">
                                <FontIcon iconName="CompletedSolid" />
                                <div>
                                    <b>{CompanyOnboardingStrings.kycComplete}</b>
                                    <p>{CompanyOnboardingStrings.kycCompleteHint}</p>
                                </div>
                                <ResultActions>
                                    <DefaultButton className="button tiny-primary-button" onClick={controller.goToIdentities}>
                                        {CompanyOnboardingStrings.backToIdentities}
                                    </DefaultButton>
                                </ResultActions>
                            </ResultPanel>
                        )}

                        {formData && (phase === "editing" || phase === "readOnly") &&
                        (
                            <React.Fragment>
                                {phase === "readOnly" &&
                                (
                                    <InfoBanner role="note">
                                        <FontIcon iconName="Lock" />
                                        <span>{CompanyOnboardingStrings.kycReadOnly}</span>
                                    </InfoBanner>
                                )}
                                {controller.errors.length > 0 &&
                                (
                                    <ErrorNotice role="alert">
                                        {controller.errors.map((message, index) => <p key={index}>{message}</p>)}
                                    </ErrorNotice>
                                )}

                                <DetailsLayout>
                                    <CompanyDetailsSectionNav sections={CompanyKycSections} />
                                    <div>
                                        <FormFieldset disabled={!controller.isEditable}>
                                            <CompanySectionCard id={sectionId(1)} number={1} title={CompanyOnboardingStrings.pepTitle} description={CompanyOnboardingStrings.pepDescription}>
                                                {yesNo("isPep", "¿Es persona políticamente expuesta?")}
                                                {formData.isPep &&
                                                (
                                                    <FieldGrid>
                                                        <Field>
                                                            {required("Nombre completo")}
                                                            {textInput("pepFullName", 200)}
                                                            {missing("pepFullName")}
                                                        </Field>
                                                        <Field>
                                                            {required("Cargo")}
                                                            {textInput("pepPosition", 200)}
                                                            {missing("pepPosition")}
                                                        </Field>
                                                    </FieldGrid>
                                                )}
                                                {yesNo("isPepRelative", "¿Es familiar de una persona políticamente expuesta?")}
                                                {formData.isPepRelative &&
                                                (
                                                    <FieldGrid>
                                                        <Field>
                                                            {required("Nombre completo del PEP")}
                                                            {textInput("pepRelativeFullName", 200)}
                                                            {missing("pepRelativeFullName")}
                                                        </Field>
                                                        <Field>
                                                            {required("Cargo del PEP")}
                                                            {textInput("pepRelativePosition", 200)}
                                                            {missing("pepRelativePosition")}
                                                        </Field>
                                                        <Field>
                                                            {required("Parentesco")}
                                                            {textInput("pepRelativeRelationship", 100)}
                                                            {missing("pepRelativeRelationship")}
                                                        </Field>
                                                    </FieldGrid>
                                                )}
                                            </CompanySectionCard>

                                            <CompanySectionCard id={sectionId(2)} number={2} title={CompanyOnboardingStrings.riskCountriesTitle} description={CompanyOnboardingStrings.riskCountriesDescription}>
                                                {yesNo("riskCountries", "¿Interviene en países de riesgo?")}
                                                {formData.riskCountries &&
                                                (
                                                    <Field as="div">
                                                        {required("Países de riesgo")}
                                                        {multiSelect("riskCountriesWhich", CountryOptions, formData.riskCountriesWhich, CompanyOnboardingStrings.riskCountriesPlaceholder,
                                                            controller.isMissing("riskCountriesWhich"), controller.handleRiskCountriesChange)}
                                                        {missing("riskCountriesWhich")}
                                                    </Field>
                                                )}
                                            </CompanySectionCard>

                                            <CompanySectionCard id={sectionId(3)} number={3} title={CompanyOnboardingStrings.corporateTitle} description={CompanyOnboardingStrings.corporateDescription}>
                                                {yesNo("listedCompany", "¿La sociedad cotiza?")}
                                                {yesNo("isBranchOrSubsidiary", "¿Es sucursal o filial participada?")}
                                                {yesNo("bearerShares", "¿Tiene acciones al portador?")}
                                            </CompanySectionCard>

                                            <CompanySectionCard id={sectionId(4)} number={4} title={CompanyOnboardingStrings.fundsTitle} description={CompanyOnboardingStrings.fundsDescription}>
                                                <YesNoRow role="radiogroup" aria-label="Actividad económica">
                                                    <span>Actividad económica <b>*</b></span>
                                                    <div>
                                                        {EconomicActivityOptions.map((option) =>
                                                        (
                                                            <Pill key={option.value} $checked={formData.economicActivity === option.value}>
                                                                <input type="radio" name="economicActivity" value={option.value} checked={formData.economicActivity === option.value} onChange={controller.handleTextChange} /> {option.text}
                                                            </Pill>
                                                        ))}
                                                    </div>
                                                </YesNoRow>
                                                {missing("economicActivity")}
                                                <FieldGrid>
                                                    <Field as="div">
                                                        {required("Origen de fondos")}
                                                        {multiSelect("fundsOrigin", FundsOriginMultiOptions, formData.fundsOrigin, CompanyOnboardingStrings.fundsOriginPlaceholder,
                                                            controller.isMissing("fundsOrigin"), controller.handleFundsOriginChange)}
                                                        {missing("fundsOrigin")}
                                                    </Field>
                                                    {formData.fundsOrigin.includes(FundsOriginInvestments) &&
                                                    (
                                                        <Field>
                                                            {required("Inversiones: especificar")}
                                                            {textInput("fundsOriginInvestmentsWhich", 200)}
                                                            {missing("fundsOriginInvestmentsWhich")}
                                                        </Field>
                                                    )}
                                                    {formData.fundsOrigin.includes(FundsOriginOther) &&
                                                    (
                                                        <Field>
                                                            {required("Otros: especificar")}
                                                            {textInput("fundsOriginOther", 200)}
                                                            {missing("fundsOriginOther")}
                                                        </Field>
                                                    )}
                                                    <Field>
                                                        {required("Ingresos anuales brutos")}
                                                        <Select name="annualGrossIncome" value={formData.annualGrossIncome} onChange={controller.handleTextChange}>
                                                            <option value="">Selecciona un tramo</option>
                                                            {AnnualGrossIncomeOptions.map((option) => <option key={option.value} value={option.value}>{option.text}</option>)}
                                                        </Select>
                                                        {missing("annualGrossIncome")}
                                                    </Field>
                                                    <Field>
                                                        <span>Volumen de carteras con Diaphanum</span>
                                                        {textInput("portfolioVolume", 15, "number", "0 €")}
                                                        {missing("portfolioVolume")}
                                                    </Field>
                                                    <Field>
                                                        <span>Aportación máxima</span>
                                                        {textInput("maxContribution", 15, "number", "0 €")}
                                                        {missing("maxContribution")}
                                                    </Field>
                                                </FieldGrid>
                                                {yesNo("providesFundsDocumentation", "¿Acredita el origen de los fondos?")}
                                                {yesNo("knowsManager", "¿Conoce al gestor de Diaphanum que tiene asignado?")}
                                                <Field>
                                                    {required("Motivos manifestados para la apertura")}
                                                    <TextArea name="relationshipOpeningReasons" value={formData.relationshipOpeningReasons} onChange={controller.handleTextChange}
                                                        maxLength={1000} placeholder="Describe brevemente el motivo de la apertura de la cuenta"
                                                    />
                                                    {missing("relationshipOpeningReasons")}
                                                </Field>
                                                <DocumentUploadBlock category="ProofOfIncome" title={CompanyOnboardingStrings.proofOfIncomeDocuments} hint={CompanyOnboardingStrings.proofOfIncomeDocumentsHint}
                                                    accept=".pdf" dropHint={CompanyOnboardingStrings.proofOfIncomeDropHint} categoryLabel={CompanyOnboardingStrings.proofOfIncomeCategory}
                                                    multiple={true} documents={controller.documents} uploading={controller.isUploading} disabled={!controller.isEditable || controller.isUploading}
                                                    onUpload={controller.handleUploadDocuments} onDelete={controller.handleDeleteDocument}
                                                />
                                            </CompanySectionCard>

                                            <CompanySectionCard id={sectionId(5)} number={5} title={CompanyOnboardingStrings.fatcaTitle} description={CompanyOnboardingStrings.fatcaDescription}>
                                                {yesNo("usCitizen", "¿Es ciudadano o entidad de los EEUU?")}
                                                {yesNo("usPersonControl", "¿La sociedad está controlada por una US Person?")}
                                                {yesNo("usResidence", "¿Tiene domicilio o residencia en EEUU?")}
                                                {yesNo("usTaxObligation", "¿Tiene obligación de pago de impuestos en EEUU?")}
                                                {yesNo("usBeneficiary", "¿Beneficiario final con tipo reducido en EEUU?")}
                                                <FieldGrid>
                                                    <Field>
                                                        <span>TIN (Taxpayer Identification Number)</span>
                                                        {textInput("usTin", 50, "text", "Solo si aplica")}
                                                    </Field>
                                                </FieldGrid>
                                            </CompanySectionCard>
                                        </FormFieldset>

                                        {controller.messages.length > 0 && <SubmitNotice role="status">{controller.messages.join(" ")}</SubmitNotice>}

                                        {controller.isEditable &&
                                        (
                                            <DetailsFooter>
                                                <CheckRow>
                                                    <input type="checkbox" checked={formData.dataTruthfulnessAccepted} onChange={(e) => controller.handleDeclarationChange(e.target.checked)} />
                                                    {CompanyOnboardingStrings.declaration}
                                                </CheckRow>
                                                <DefaultButton className="button tiny-secondary-button" onClick={controller.goToCompanyDetails} disabled={controller.isSaving}>
                                                    {CompanyOnboardingStrings.previousStep}
                                                </DefaultButton>
                                                <DefaultButton className="button tiny-secondary-button" onClick={controller.handleSaveDraft} disabled={controller.isSaving || controller.isUploading}>
                                                    {controller.isSaving ? CompanyOnboardingStrings.savingDraft : CompanyOnboardingStrings.saveDraft}
                                                </DefaultButton>
                                                <DefaultButton className="button tiny-primary-button" onClick={controller.handleSendToReview} disabled={!controller.canSend}>
                                                    {controller.isSaving && <Spinner size={SpinnerSize.small} style={{marginRight: "8px"}} />}
                                                    {CompanyOnboardingStrings.sendToReview}
                                                </DefaultButton>
                                            </DetailsFooter>
                                        )}
                                        {phase === "readOnly" &&
                                        (
                                            <DetailsFooter>
                                                <DefaultButton className="button tiny-secondary-button" onClick={controller.goToCompanyDetails}>
                                                    {CompanyOnboardingStrings.previousStep}
                                                </DefaultButton>
                                            </DetailsFooter>
                                        )}
                                    </div>
                                </DetailsLayout>
                            </React.Fragment>
                        )}
                    </MainCont>
                </ThemeProvider>
            </Cont>
        </DocumentTitle>
    );
};

export default CompanyKycScreen;
